import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedPhoneNumber from 'I18n/components/FormattedPhoneNumber';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import unescapedText from 'I18n/utils/unescapedText';
import { CallingIcon, EmailIcon, MessagesIcon, SocialBlockFacebookIcon, WebsiteIcon } from 'foundations-assets';
import { Flex } from 'foundations-components/components/Flex';
import { CopyButton } from 'foundations-components/transitional/components/CopyButton';
import { DescriptionList } from 'foundations-components/transitional/components/DescriptionList';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
import { Link } from 'foundations-components/transitional/components/Link';
import { getFormattedUrl } from 'marketplace-ui-apps-core/common/utils/urls';
import { Fragment, useCallback, useMemo } from 'react';
import { useDetailsPageContext } from '../context/DetailsPageContext';
import { Languages } from './Languages';
import { getUserHasZorseChatScope, getUserHasZorseWebticketScope } from './utils';
// @ts-expect-error JS file
import { openHelpWidget } from './openHelpWidget';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const NoIcon = () => null;
const getEventSelectorId = id => `marketplace-mirrored__app__${id}`;
function LinkItem({
  children,
  href,
  dataAnalyticsId,
  onClick
}) {
  return /*#__PURE__*/_jsx(Link, {
    "data-hs-analytics-id": dataAnalyticsId,
    external: true,
    ExternalIcon: NoIcon,
    href: href,
    onClick: onClick,
    children: children
  });
}
export function SupportTab({
  chatUrl,
  email,
  facebookUrl,
  homemade,
  onTrackInteraction = () => {},
  phone,
  supportLanguages,
  supportUrl
}) {
  const {
    appContext,
    userInfo
  } = useDetailsPageContext();
  const isCustomer = appContext === 'app';
  const hasZorseWebticketScope = getUserHasZorseWebticketScope(userInfo);
  const hasZorseChatScope = getUserHasZorseChatScope(userInfo);
  const trackInteraction = useCallback(action => onTrackInteraction(action, {
    section: 'contact'
  }), [onTrackInteraction]);
  const renderedEmailSupportLink = useMemo(() => {
    const emailSupportIconText = /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx(IconHolder, {
        icon: EmailIcon
      }), /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.infoCard.contact.emailSupportLinkText"
      })]
    });
    if (homemade && isCustomer && hasZorseWebticketScope) {
      return /*#__PURE__*/_jsx(LinkItem, {
        dataAnalyticsId: getEventSelectorId('SIDEBAR-EMAIL-SUPPORT'),
        onClick: () => {
          trackInteraction('click copy email address');
          openHelpWidget({
            create: true
          });
        },
        children: emailSupportIconText
      });
    } else if (email) {
      return /*#__PURE__*/_jsx(CopyButton, {
        "data-hs-analytics-id": getEventSelectorId('SIDEBAR-EMAIL-SUPPORT'),
        hint: unescapedText(`detailsPage.infoCard.contact.emailCopyButtonHint`),
        onClick: () => trackInteraction('click copy email address'),
        value: email,
        use: "link",
        children: emailSupportIconText
      });
    }
    return null;
  }, [email, hasZorseWebticketScope, homemade, isCustomer, trackInteraction]);
  const renderedLiveChatLink = useMemo(() => {
    const liveChatIconText = /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx(IconHolder, {
        icon: MessagesIcon
      }), /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.infoCard.contact.liveChatLinkText"
      })]
    });
    if (homemade && isCustomer && hasZorseChatScope) {
      return /*#__PURE__*/_jsx(LinkItem, {
        dataAnalyticsId: getEventSelectorId('SIDEBAR-LIVE-CHAT'),
        onClick: () => {
          trackInteraction('click live chat link');
          openHelpWidget({
            chat: true
          });
        },
        children: liveChatIconText
      });
    } else if (chatUrl) {
      return /*#__PURE__*/_jsx(LinkItem, {
        dataAnalyticsId: getEventSelectorId('SIDEBAR-LIVE-CHAT'),
        href: getFormattedUrl(chatUrl),
        onClick: () => trackInteraction('click live chat link'),
        children: liveChatIconText
      });
    }
    return null;
  }, [chatUrl, hasZorseChatScope, homemade, isCustomer, trackInteraction]);
  const renderedPhoneNumber = useMemo(() => {
    if (!phone) return null;
    const [phoneNumberBase, extension] = phone.split('-');
    const formattedPhoneNumberBase = /*#__PURE__*/_jsx(FormattedPhoneNumber, {
      value: phoneNumberBase
    });
    return extension ? /*#__PURE__*/_jsxs(LinkItem, {
      href: `tel:${phoneNumberBase}`,
      children: [/*#__PURE__*/_jsx(IconHolder, {
        icon: CallingIcon
      }), /*#__PURE__*/_jsx(FormattedReactMessage, {
        message: "detailsPage.infoCard.contact.phoneWithExtension",
        options: {
          phoneNumberBase: /*#__PURE__*/_jsx(Link, {
            href: `tel:${phoneNumberBase}`,
            children: formattedPhoneNumberBase
          }),
          extension
        }
      })]
    }) : /*#__PURE__*/_jsxs(Link, {
      href: `tel:${phoneNumberBase}`,
      children: [/*#__PURE__*/_jsx(IconHolder, {
        icon: CallingIcon
      }), formattedPhoneNumberBase]
    });
  }, [phone]);
  return /*#__PURE__*/_jsxs(Flex, {
    flexDirection: "column",
    alignItems: "start",
    gap: "100",
    children: [renderedEmailSupportLink, supportUrl && /*#__PURE__*/_jsxs(LinkItem, {
      dataAnalyticsId: getEventSelectorId('SIDEBAR-SUPPORT'),
      href: getFormattedUrl(supportUrl),
      onClick: () => trackInteraction('click support website link'),
      children: [/*#__PURE__*/_jsx(IconHolder, {
        icon: WebsiteIcon
      }), /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.infoCard.contact.supportWebsiteLinkText"
      })]
    }), renderedLiveChatLink, facebookUrl && /*#__PURE__*/_jsxs(LinkItem, {
      dataAnalyticsId: getEventSelectorId('SIDEBAR-FACEBOOK'),
      href: getFormattedUrl(facebookUrl),
      onClick: () => trackInteraction('click facebook link'),
      children: [/*#__PURE__*/_jsx(IconHolder, {
        icon: SocialBlockFacebookIcon
      }), /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.infoCard.contact.facebookLinkText"
      })]
    }), renderedPhoneNumber, supportLanguages && /*#__PURE__*/_jsxs(DescriptionList, {
      marginBlockStart: "400",
      children: [/*#__PURE__*/_jsx("dt", {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.infoCard.contact.supportLanguages"
        })
      }), /*#__PURE__*/_jsx("dd", {
        children: /*#__PURE__*/_jsx(Languages, {
          languages: supportLanguages
        })
      })]
    })]
  });
}